import { Component, computed, effect, inject, input, model, output, signal, DestroyRef } from '@angular/core';

import {
  InputFieldComponent,
  GenericDropdownComponent,
  PhoneNumberFieldComponent,
  TitleEnum,
  FileDropComponent,
  GalleryImage,
  FileUploadService
} from '@zayan-erp/shared';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { CoBuyer, CreateCoBuyer, UpdateCoBuyer } from '../../models/co-buyer.models';
import { SalesService } from '../../services/sales.service';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { ProjectsService } from '@zayan-erp/home-building';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProspectContact } from '../../models/prospect.models';
import { tap, switchMap, map, take, filter } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { HttpEventType } from '@angular/common/http';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { SalesApsService } from '@zayan-erp/sales';
import { ConfirmHelperService } from '@zayan-erp/shared';

@Component({
  selector: 'co-buyer-form',
  imports: [
    InputFieldComponent,
    GenericDropdownComponent,
    PhoneNumberFieldComponent,
    FileDropComponent,
    ReactiveFormsModule,
    DatePickerModule,
    ConfirmDialogModule
  ],
  templateUrl: './co-buyer-form.component.html',
  providers: []
})
export class CoBuyerFormComponent {
  readonly #salesService = inject(SalesService);
  readonly #projectsService = inject(ProjectsService);
  readonly #modalService = inject(NgbModal);
  readonly #destroyRef = inject(DestroyRef);
  readonly #fileUploadService = inject(FileUploadService);
  readonly #confirmationService = inject(ConfirmHelperService);
  readonly _salesApsService = inject(SalesApsService);
  readonly prospectId = input<string | null>();

  readonly selectedCoBuyerId = model<string | null>(null, { alias: 'selectedCoBuyerInput' });
  readonly effectiveProspectId = computed(
    () => this._salesApsService.currentDeal()?.prospect?.id! ?? this.prospectId()!
  );
  readonly onSubmit = input<Observable<void>>();

  readonly reload = output<void>();

  readonly cities = toSignal(this.#projectsService.readCitiesPublic(), { initialValue: [] });

  readonly citiesDisplayList = computed(() =>
    this.cities().map(city => ({
      id: city.id,
      firstLabel: city.cityName,
      secondLabel: city.cityCode
    }))
  );
  titlesList = computed(() =>
    Object.values(TitleEnum).map(title => ({
      id: title,
      firstLabel: title
    }))
  );

  readonly maxDate = new Date();

  readonly cardImage = signal<(GalleryImage & { progress: number | null; s3Key: string; documentId: string }) | null>(
    null
  );

  readonly coBuyerForm = new FormGroup({
    title: new FormControl<string | null>(null),
    firstName: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(50)]),
    middleName: new FormControl<string | null>(null, [Validators.maxLength(50)]),
    lastName: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(50)]),
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
    ssn: new FormControl<string | null>(null),
    dateOfBirth: new FormControl<Date | null>(null),
    addToMailingList: new FormControl<boolean>(true, { nonNullable: true }),
    includeInContract: new FormControl<boolean>(true, { nonNullable: true }),
    isActive: new FormControl<boolean>(true, { nonNullable: true }),
    address: new FormGroup({
      addressLine1: new FormControl<string | null>(null, [Validators.required]),
      addressLine2: new FormControl<string | null>(null),
      suite: new FormControl<string | null>(null),
      cityId: new FormControl<string | null>(null, [Validators.required]),
      postalCode: new FormControl<string | null>(null, [Validators.required])
    }),
    phone: new FormControl<ProspectContact[] | null>(null)
  });

  readonly #coBuyerResource = rxResourceFixed({
    params: () => ({ id: this.selectedCoBuyerId() }),
    defaultValue: null,
    stream: ({ params }) =>
      params.id
        ? this.#salesService.readCoBuyerById(params.id).pipe(tap(coBuyer => this.patchFormWithCoBuyer(coBuyer)))
        : of(null)
  });

  readonly #prospectResource = rxResourceFixed({
    params: () => ({ id: this.effectiveProspectId() }),
    defaultValue: null,
    stream: ({ params }: { params: { id: string | null } }) =>
      params.id ? this.#salesService.readProspectById(params.id) : of(null)
  });

  constructor() {
    effect(() => {
      const onSubmitObservable = this.onSubmit();
      if (onSubmitObservable) {
        onSubmitObservable.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
          this.submitCoBuyerForm();
        });
      }
    });
  }

  submitCardImage(): void {
    if (!this.cardImage()) {
      return;
    }

    const currentImage = this.cardImage()!;

    this.#fileUploadService
      .getPrivateAWSSignedUrl(currentImage.file!)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3(awsData).pipe(
            tap((uploadEvent: any) => {
              if (uploadEvent?.type === HttpEventType.UploadProgress && uploadEvent.total) {
                const progressValue = Math.round((100 * uploadEvent.loaded) / uploadEvent.total);
                this.cardImage.set({
                  ...currentImage,
                  progress: progressValue,
                  s3Key: awsData.s3Key,
                  documentId: awsData.documentId
                });
              }
            }),
            map(() => awsData)
            // filter((uploadEvent: any) => uploadEvent?.type === HttpEventType.Response),
            // take(1)
          )
        ),
        switchMap(awsData => this.#fileUploadService.getPresignedUrlFromS3Key(awsData.s3Key, awsData.fileName)),
        switchMap(imageUrl => this.#salesService.scanIdentityCard(imageUrl, undefined))
      )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(scanResult => {
        this.insertScannedDetails(scanResult);
        this.cardImage.set(null);
        this.#confirmationService.showModal({
          icon: 'confirm-icon',
          header: 'Verify Details',
          message: 'Please verify the scanned details before saving the co-buyer.',
          acceptLabel: 'OK',
          acceptSeverity: 'primary',
          rejectLabel: ''
        });
      });
  }

  insertScannedDetails(scanResult: any): void {
    this.coBuyerForm.patchValue({
      title: scanResult.data.title ?? null,
      firstName: scanResult.data.firstName,
      middleName: scanResult.data.middleName ?? null,
      lastName: scanResult.data.lastName,
      ssn: scanResult.data.ssn ?? null,
      address: {
        addressLine1: scanResult.data?.addressLine1,
        addressLine2: scanResult.data?.addressLine2,
        postalCode: scanResult.data?.zipCode,
        cityId: scanResult.data?.cityId,
        suite: scanResult.data?.suite
      },
      email: scanResult.data?.email ?? null,
      ...(scanResult.data?.phoneNumber
        ? {
            phone: [
              {
                phoneNumber: scanResult.data?.phoneNumber,
                type: 'Mobile'
              }
            ]
          }
        : {}),
      dateOfBirth: scanResult.data.dateOfBirth ? new Date(scanResult.data.dateOfBirth) : null
    });
  }

  onCardFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.cardImage.set({
        file,
        fileSrcUrl: URL.createObjectURL(file),
        selected: false,
        progress: null,
        s3Key: '',
        documentId: ''
      });

      this.submitCardImage();
    }
  }

  onCardFileDrop(event: NgxFileDropEntry[]): void {
    for (const dropped of event) {
      if (dropped.fileEntry.isFile) {
        const fileEntry = dropped.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          this.cardImage.set({
            file,
            fileSrcUrl: URL.createObjectURL(file),
            selected: false,
            progress: null,
            s3Key: '',
            documentId: ''
          });

          this.submitCardImage();
        });
      }
    }
  }

  private patchFormWithCoBuyer(coBuyer: CoBuyer): void {
    this.coBuyerForm.patchValue({
      title: coBuyer.title ?? null,
      firstName: coBuyer.firstName,
      middleName: coBuyer.middleName ?? null,
      lastName: coBuyer.lastName,
      email: coBuyer.email,
      ssn: coBuyer.ssn ?? null,
      dateOfBirth: coBuyer.dateOfBirth ? new Date(coBuyer.dateOfBirth) : null,
      addToMailingList: coBuyer.addToMailingList,
      includeInContract: coBuyer.includeInContract,
      isActive: coBuyer.isActive,
      address: {
        addressLine1: coBuyer.address.addressLine1,
        addressLine2: coBuyer.address.addressLine2 ?? null,
        postalCode: coBuyer.address.postalCode,
        cityId: coBuyer.address.cityId,
        suite: coBuyer.address.suite ?? null
      },
      phone: coBuyer.phone ? [coBuyer.phone] : null
    });
    this.coBuyerForm.markAsPristine();
  }

  private buildCoBuyerPayload(formData: ReturnType<typeof this.coBuyerForm.getRawValue>): CreateCoBuyer {
    return {
      prospectId: this.effectiveProspectId(),
      title: formData.title || undefined,
      firstName: formData.firstName!,
      middleName: formData.middleName || undefined,
      lastName: formData.lastName!,
      email: formData.email!,
      ssn: formData.ssn || undefined,
      dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth.toISOString() : undefined,
      addToMailingList: formData.addToMailingList,
      includeInContract: formData.includeInContract,
      isActive: formData.isActive,
      address: {
        addressLine1: formData.address.addressLine1!,
        addressLine2: formData.address.addressLine2 || undefined,
        suite: formData.address.suite || undefined,
        cityId: formData.address.cityId!,
        postalCode: formData.address.postalCode!
      },
      phone: {
        phoneNumber: this.normalizePhoneNumber(formData.phone?.[0]?.phoneNumber) || '',
        type: formData.phone?.[0]?.type || ''
      }
    };
  }

  private normalizePhoneNumber(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.replace(/\s+/g, '').trim();
  }

  private handleSuccess(): void {
    this.reload.emit();
    this.coBuyerForm.reset();
    this.selectedCoBuyerId.set(null);
    this.#modalService.dismissAll();
  }

  submitCoBuyerForm(): void {
    if (!this.coBuyerForm.valid) {
      this.coBuyerForm.markAllAsTouched();
      return;
    }

    const formData = this.coBuyerForm.getRawValue();
    const coBuyerData = this.buildCoBuyerPayload(formData);
    const selectedId = this.selectedCoBuyerId();

    const request$ = selectedId
      ? this.#salesService.updateCoBuyer(selectedId, { id: selectedId, ...coBuyerData } as UpdateCoBuyer)
      : this.#salesService.createCoBuyer(coBuyerData);

    request$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.handleSuccess());
  }

  copyProspectDetails() {
    const prospect = this.#prospectResource.value();
    if (prospect && prospect.address) {
      const address = prospect.address;
      this.coBuyerForm.patchValue({
        address: {
          addressLine1: address.addressLine1 || null,
          addressLine2: address.addressLine2 ?? null,
          suite: address.suite ?? null,
          cityId: address.cityId || null,
          postalCode: address.postalCode
        }
      });
    }
  }
}
